import React, { useState, useEffect } from 'react';

/** Inline date parser (matches the one in UnifiedActivities) */
const parseDate = (raw) => {
  if (!raw) return null;
  const d = typeof raw === 'string'
    ? (raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00`))
    : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/* ─── Nested assignment row ─── */
function AssignmentRow({ item, formatDate, onItemClick, onToggle, completingId }) {
  const id = item.id;
  const [justToggled, setJustToggled] = useState(false);
  const isOverdue = (() => {
    const d = parseDate(item.date);
    return d && d < new Date() && !item.completed;
  })();

  // Clear animation class after it plays
  useEffect(() => {
    if (!justToggled) return;
    const t = setTimeout(() => setJustToggled(false), 400);
    return () => clearTimeout(t);
  }, [justToggled]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setJustToggled(true);
    onToggle(item);
  };

  return (
    <div
      className={`eg-assignment-row ${item.completed ? 'eg-assignment-row--completed' : ''} ${isOverdue ? 'eg-assignment-row--overdue' : ''}`}
      onClick={() => onItemClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(item); } }}
    >
      {/* Completion toggle */}
      <button
        className={`eg-assignment-toggle ${item.completed ? 'is-complete' : 'is-incomplete'} ${justToggled ? 'just-toggled' : ''}`}
        onClick={handleToggle}
        disabled={completingId === id}
        aria-label={item.completed ? 'Mark incomplete' : 'Mark complete'}
        title={item.completed ? 'Mark Incomplete' : 'Mark Complete'}
      >
        {item.completed ? (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="var(--status-success, #4ade80)" />
            <path d="M8 12l3 3 5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="var(--border-default)" strokeWidth="1.5" fill="none" />
          </svg>
        )}
      </button>

      <div className="eg-assignment-body">
        <span className="eg-assignment-title">{item.title}</span>
        {item.duplicateCount > 1 && (
          <span className="eg-assignment-dupe-badge" title={`${item.duplicateCount} duplicates merged`}>
            ×{item.duplicateCount}
          </span>
        )}
      </div>

      <div className="eg-assignment-meta">
        <span className="eg-assignment-date">{formatDate(item.date)}</span>
        {isOverdue && <span className="eg-overdue-chip">Overdue</span>}
      </div>
    </div>
  );
}

/* ─── Event group (expandable) ─── */
function EventGroup({ event, expanded, toggle, formatDate, onItemClick, onEventClick, onToggle, completingId, highlighted, urgency }) {
  const overdueCount = event.assignments.filter(a => {
    const d = parseDate(a.date);
    return d && d < new Date() && !a.completed;
  }).length;

  const completedCount = event.assignments.filter(a => a.completed).length;
  const totalAssignments = event.assignments.length;

  const isPastEvent = (() => {
    const d = parseDate(event.date);
    return d && d < new Date();
  })();

  const urgencyClass = urgency && urgency !== 'upcoming' ? `event-group--${urgency}` : '';

  return (
    <div id={`event-group-${event.id}`} className={`event-group ${expanded ? 'event-group--expanded' : ''} ${isPastEvent ? 'event-group--past' : ''} ${highlighted ? 'event-group--highlighted' : ''} ${urgencyClass}`}>
      {/* Event header row */}
      <div
        className="event-row"
        onClick={() => toggle(event.id)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(event.id); } }}
      >
        {/* Calendar icon */}
        <div className="event-row-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>

        <div className="event-row-body">
          <div
            className="event-row-title"
            onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(event); }}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onEventClick && onEventClick(event); } }}
            title="View event details"
          >
            {event.title}
          </div>
          <div className="event-row-date">{formatDate(event.date)}</div>
        </div>

        <div className="event-row-badges">
          {totalAssignments > 0 && (
            <span className="eg-badge eg-badge--due">
              {completedCount === totalAssignments
                ? `${totalAssignments} done`
                : `${totalAssignments - completedCount} due`}
            </span>
          )}
          {overdueCount > 0 && (
            <span className="eg-badge eg-badge--overdue">
              {overdueCount} overdue
            </span>
          )}
        </div>

        <span className="event-row-hint">Details</span>

        <div className={`event-row-chevron ${expanded ? 'event-row-chevron--open' : ''}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </div>
      </div>

      {/* Nested assignments */}
      {expanded && totalAssignments > 0 && (
        <div className="event-group-nested">
          {event.assignments.map((a, idx) => (
            <AssignmentRow
              key={`${a.id}-${idx}`}
              item={a}
              formatDate={formatDate}
              onItemClick={onItemClick}
              onToggle={onToggle}
              completingId={completingId}
            />
          ))}
        </div>
      )}

      {expanded && totalAssignments === 0 && (
        <div className="event-group-nested event-group-nested--empty">
          <span className="eg-empty-text">No assignments linked to this event</span>
        </div>
      )}
    </div>
  );
}

export default React.memo(EventGroup);
